import type { CSSProperties } from "react";

/**
 * Shared chrome for the app's bottom sheets / modal cards.
 *
 * Every overlay in the app (student details, lesson details, the enrollment
 * picker, the lesson and student forms, the reversal dialog) renders the same
 * pattern: a full-screen scrim with a card snapped to the bottom edge. It used
 * to be copy-pasted into each of them, which meant each copy could drift — and
 * it did: the card was `overflowY: "auto"` only, so on narrow screens a row
 * that could not shrink silently pushed the sheet's content past its right
 * edge, where it was clipped and unreachable.
 *
 * Both constants live here so the layout contract is stated once.
 */

/** Full-screen scrim behind a bottom sheet. */
export const SHEET_OVERLAY_STYLE: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(20,16,12,0.45)",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  zIndex: 60,
};

/**
 * The sheet card itself. Spread it into `<Card style={...}>`; the card's
 * `padding` is kept, with the bottom edge raised to clear the iOS home
 * indicator when the viewport reports a safe-area inset.
 */
export const SHEET_STYLE: CSSProperties = {
  width: "100%",
  // A flex item defaults to `min-width: auto`, so a single row that cannot
  // shrink (a long badge, a segmented control, a wide ledger row) was enough
  // to stretch the whole card past the phone frame. `min-width: 0` keeps the
  // card at the frame width and forces the rows to handle their own width.
  minWidth: 0,
  maxWidth: "var(--container-app)",
  maxHeight: "90%",
  // Reachable on short viewports (landscape phone, keyboard open) instead of
  // hanging below the fold.
  overflowY: "auto",
  // `overflow-y: auto` alone also computes `overflow-x` to `auto`, which made
  // the sheet an invisible horizontal scroller: overflowing rows were clipped
  // at the padding edge with no visual affordance. Rows must fit; the only
  // sideways scroller is the one that advertises itself (SegmentedControl).
  overflowX: "hidden",
  // Don't chain a scroll that reaches the sheet's edge into the page behind it.
  overscrollBehavior: "contain",
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  paddingBottom: "max(var(--space-5), env(safe-area-inset-bottom, 0px))",
};
